import AxiosConfig from '../utils/AxiosConfig';

class B2BClientService {
  normalizeClientsResponse(payload) {
    if (Array.isArray(payload)) {
      return {
        clients: payload,
        totalCount: payload.length,
        totalPages: 1,
        currentPage: 1,
        pageSize: payload.length
      };
    }

    return {
      clients: Array.isArray(payload?.clients) ? payload.clients : [],
      totalCount: payload?.totalCount || 0,
      totalPages: payload?.totalPages || 0,
      currentPage: payload?.currentPage || 1,
      pageSize: payload?.pageSize || 0
    };
  }

  // Obtener todos los clientes B2B
  async getClients(page = 1, pageSize = 10, searchTerm = '', options = {}) {
    try {
      const {
        sortField,
        sortDirection,
        filterField,
        filterOperator,
        filterValue,
        balanceFilter
      } = options;

      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString()
      });
      if (searchTerm && typeof searchTerm === 'string' && searchTerm.trim()) {
        params.append('search', searchTerm.trim());
      }
      if (sortField) params.append('sortField', sortField);
      if (sortDirection) params.append('sortDirection', sortDirection);
      if (filterField) params.append('filterField', filterField);
      if (filterOperator) params.append('filterOperator', filterOperator);
      if (filterValue !== undefined && filterValue !== null && String(filterValue).trim() !== '') {
        params.append('filterValue', String(filterValue).trim());
      }
      if (balanceFilter) params.append('balanceFilter', balanceFilter);
      const response = await AxiosConfig.get(`/b2b/clients?${params}`);
      return this.normalizeClientsResponse(response.data.data);
    } catch (error) {
      console.error('Error fetching B2B clients:', error);
      throw error;
    }
  }

  // Obtener un cliente específico por ID
  async getClientById(clientId) {
    try {
      const response = await AxiosConfig.get(`/b2b/clients/${clientId}`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching B2B client:', error);
      throw error;
    }
  }

  // Crear un nuevo cliente B2B
  async createClient(clientData) {
    try {
      const response = await AxiosConfig.post('/b2b/clients', clientData);
      return response.data.data;
    } catch (error) {
      console.error('Error creating B2B client:', error);
      throw error;
    }
  }

  // Actualizar un cliente B2B
  async updateClient(clientId, clientData) {
    try {
      const response = await AxiosConfig.put(`/b2b/clients/${clientId}`, clientData);
      return response.data.data;
    } catch (error) {
      console.error('Error updating B2B client:', error);
      throw error;
    }
  }

  // Eliminar un cliente B2B
  async deleteClient(clientId) {
    try {
      const response = await AxiosConfig.delete(`/b2b/clients/${clientId}`);
      return response.data.data;
    } catch (error) {
      console.error('Error deleting B2B client:', error);
      throw error;
    }
  }

  // Obtener historial de un cliente (proformas, pedidos, pagos)
  async getClientHistory(clientId) {
    try {
      const response = await AxiosConfig.get(`/b2b/clients/${clientId}/history`);
      return response.data;
    } catch (error) {
      console.error('Error fetching client history:', error);
      throw error;
    }
  }

  // Obtener excepciones de precio de un cliente
  async getClientPriceExceptions(clientId) {
    try {
      const response = await AxiosConfig.get(`/b2b/clients/${clientId}/price-exceptions`);
      return response.data;
    } catch (error) {
      console.error('Error fetching client price exceptions:', error);
      throw error;
    }
  }

  // Actualizar excepciones de precio de un cliente
  async updateClientPriceExceptions(clientId, priceExceptions) {
    try {
      const response = await AxiosConfig.put(`/b2b/clients/${clientId}/price-exceptions`, {
        priceExceptions
      });
      return response.data;
    } catch (error) {
      console.error('Error updating client price exceptions:', error);
      throw error;
    }
  }

  // Buscar clientes por criterio
  async searchClients(searchTerm) {
    try {
      const response = await AxiosConfig.get('/b2b/clients/search', {
        params: { q: searchTerm }
      });
      return response.data;
    } catch (error) {
      console.error('Error searching clients:', error);
      throw error;
    }
  }

  // Obtener clientes con saldo pendiente
  async getClientsWithPendingBalance() {
    try {
      const response = await AxiosConfig.get('/b2b/clients/pending-balance');
      return response.data;
    } catch (error) {
      console.error('Error fetching clients with pending balance:', error);
      throw error;
    }
  }

  async getAvailableProducts(locationId = null) {
    try {
      const response = await AxiosConfig.get('/b2b/clients/available-products', {
        params: locationId ? { locationId } : {}
      });
      return response.data.data;
    } catch (error) {
      console.error('Error fetching available B2B products:', error);
      throw error;
    }
  }

  async getOrderProducts(clientId, branchId) {
    try {
      const response = await AxiosConfig.get(`/b2b/clients/${clientId}/order-products`, {
        params: { branchId }
      });
      const data = response.data.data || {};
      const products = Array.isArray(data.products) ? data.products : [];

      return {
        ...data,
        products: products.map(product => ({
          ...product,
          price: product.unitPrice ?? product.price ?? 0,
          unitPrice: product.unitPrice ?? product.price ?? 0,
          finalUnitPrice: product.finalUnitPrice ?? product.unitPrice ?? product.price ?? 0,
          discountValue: product.discountValue ?? 0,
          discountType: product.discountType || 'PERCENTAGE'
        }))
      };
    } catch (error) {
      console.error('Error fetching B2B order products:', error);
      throw error;
    }
  }

  async getPortalAccountStatus(clientId) {
    try {
      const response = await AxiosConfig.get(`/b2b/clients/${clientId}/portal-account`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching B2B portal account status:', error);
      throw error;
    }
  }

  async sendPortalInvitation(clientId, payload = {}) {
    try {
      const response = await AxiosConfig.post(`/b2b/clients/${clientId}/portal-invitation`, payload);
      return response.data.data;
    } catch (error) {
      console.error('Error sending B2B portal invitation:', error);
      throw error;
    }
  }

  async getCommercialProfiles() {
    try {
      const response = await AxiosConfig.get('/b2b/commercial-profiles');
      return response.data.data || [];
    } catch (error) {
      console.error('Error fetching B2B commercial profiles:', error);
      throw error;
    }
  }

  async getCommercialProfileById(profileId) {
    try {
      const response = await AxiosConfig.get(`/b2b/commercial-profiles/${profileId}`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching B2B commercial profile:', error);
      throw error;
    }
  }

  async createCommercialProfile(profileData) {
    try {
      const response = await AxiosConfig.post('/b2b/commercial-profiles', profileData);
      return response.data.data;
    } catch (error) {
      console.error('Error creating B2B commercial profile:', error);
      throw error;
    }
  }

  async updateCommercialProfile(profileId, profileData) {
    try {
      const response = await AxiosConfig.put(`/b2b/commercial-profiles/${profileId}`, profileData);
      return response.data.data;
    } catch (error) {
      console.error('Error updating B2B commercial profile:', error);
      throw error;
    }
  }

  async deleteCommercialProfile(profileId) {
    try {
      const response = await AxiosConfig.delete(`/b2b/commercial-profiles/${profileId}`);
      return response.data.data;
    } catch (error) {
      console.error('Error deleting B2B commercial profile:', error);
      throw error;
    }
  }

  // Validar datos de cliente
  validateClientData(clientData) {
    const errors = {};

    if (!clientData.businessName || clientData.businessName.trim() === '') {
      errors.businessName = 'La razón social es requerida';
    }

    if (!clientData.rut || clientData.rut.trim() === '') {
      errors.rut = 'El RUT es requerido';
    }

    if (!clientData.address || clientData.address.trim() === '') {
      errors.address = 'La dirección es requerida';
    }

    if (!clientData.mainContact || clientData.mainContact.trim() === '') {
      errors.mainContact = 'El contacto principal es requerido';
    }

    if (!clientData.phone || clientData.phone.trim() === '') {
      errors.phone = 'El teléfono es requerido';
    }

    if (clientData.globalDiscount !== undefined && (clientData.globalDiscount < 0 || clientData.globalDiscount > 100)) {
      errors.globalDiscount = 'El descuento global debe estar entre 0 y 100';
    }

    if (clientData.creditDays !== undefined && clientData.creditDays < 0) {
      errors.creditDays = 'Los días de crédito deben ser mayores o iguales a 0';
    }

    if (clientData.creditLimit !== undefined && clientData.creditLimit < 0) {
      errors.creditLimit = 'El límite de crédito debe ser mayor o igual a 0';
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  }

  // Formatear datos de cliente para envío
  formatClientDataForAPI(clientData) {
    return {
      businessName: clientData.businessName?.trim(),
      rut: clientData.rut?.trim(),
      address: clientData.address?.trim(),
      mainContact: clientData.mainContact?.trim(),
      phone: clientData.phone?.trim(),
      email: clientData.email?.trim() || null,
      globalDiscount: clientData.globalDiscount || 0,
      creditDays: clientData.creditDays || 0,
      creditLimit: clientData.creditLimit || null,
      priceExceptions: clientData.priceExceptions || []
    };
  }

  // Calcular saldo de cliente
  calculateClientBalance(client) {
    const totalAmount = client.totalAmount || 0;
    const paidAmount = client.paidAmount || 0;
    return totalAmount - paidAmount;
  }

  // Determinar estado de cuenta del cliente
  determineAccountStatus(client) {
    const balance = this.calculateClientBalance(client);
    
    if (balance === 0) {
      return 'up_to_date';
    } else if (balance > 0) {
      // Verificar si hay pagos vencidos
      const hasOverduePayments = client.orders?.some(order => {
        if (!order.dueDate) return false;
        return new Date(order.dueDate) < new Date() && order.paymentStatus === 'unpaid';
      });
      
      return hasOverduePayments ? 'overdue_debt' : 'pending_debt';
    } else {
      return 'credit_balance'; // Saldo a favor
    }
  }
}

export default new B2BClientService();
